import type { PartyPrivate, Player, Room, RoomIdentity, SketchAlbum, SketchTask, TriviaScoreboard } from "./types";

export type RoomLiveStatus = {
  serverNow?: number;
  revision: number;
  mode: "full" | "compact";
  heartbeatMs: 5_000 | 30_000;
  playerCount: number;
  responseCount: number;
  top10: Array<{ id: string; name: string; avatar?: string; premium?: boolean; score: number; rank: number }>;
  lobbyRosterVersion: number;
};

export type CloudflareSnapshot = {
  room: Room;
  players: Player[];
  lobbyRosterNextCursor?: string | null;
  lobbyRosterVersion?: number;
  liveStatus?: RoomLiveStatus;
  me?: Player;
  identity: RoomIdentity;
  scoreboard: TriviaScoreboard | null;
  partyPrivate?: PartyPrivate | null;
  sketchTask?: SketchTask | null;
  sketchAlbums?: SketchAlbum[];
  sketchAlbumPages?: Record<string, string>;
  imageAssets?: Record<string, string>;
  /** Connected players, for Night Before GFC rooms only. */
  onlinePlayerIds?: string[];
};
export type CloudflareRoomState = {
  clockOffset?: number;
  snapshot: CloudflareSnapshot | null;
  loading: boolean;
  connected: boolean;
  error: string;
  closedReason?: string;
  closedMessage?: string;
  removed: boolean;
};
export const emptyCloudflareState: CloudflareRoomState = { snapshot: null, loading: true, connected: false, error: "", removed: false };

export function roomsApiUrl() {
  const configured = import.meta.env.VITE_ROOMS_API_URL?.trim();
  const origin = configured || (import.meta.env.DEV && import.meta.env.MODE !== "test" && typeof window !== "undefined"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname) ? "http://127.0.0.1:8787" : "");
  try {
    const url = new URL(origin);
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.search || url.hash || url.pathname !== "/") throw new Error("Invalid room origin");
    return url.origin;
  } catch {
    throw roomError("failed-precondition", "The Cloudflare room service is not configured. Set VITE_ROOMS_API_URL to the room Worker origin.");
  }
}

function roomError(code: string, message: string) {
  return Object.assign(new Error(message), { code: code.startsWith("functions/") ? code : `functions/${code}` });
}

async function credentials(forceRefresh = false) {
  const firebase = await import("./firebase");
  const user = firebase.auth.currentUser;
  if (!user) throw roomError("unauthenticated", "Sign in before joining a room.");
  const [idToken, appCheckToken] = await Promise.all([
    user.getIdToken(forceRefresh),
    // A blocked reCAPTCHA must not block play: send no token and let the server decide.
    firebase.appCheck ? import("firebase/app-check").then(({ getToken }) => getToken(firebase.appCheck!, false)).then(({ token }) => token, () => undefined) : undefined,
  ]);
  return { uid: user.uid, idToken, appCheckToken, isCurrent: () => firebase.auth.currentUser === user };
}

async function httpRpc<T>(code: string, method: string, data: Record<string, unknown>, timeout = 30_000, receiveSnapshot?: (snapshot: CloudflareSnapshot, userId: string) => void): Promise<T> {
  const { uid, idToken, appCheckToken, isCurrent } = await credentials();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(`${roomsApiUrl()}/rooms/${encodeURIComponent(code)}/rpc`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}) },
      body: JSON.stringify({ method, data }), signal: controller.signal,
    });
    const body = await response.json() as { result?: T; snapshot?: CloudflareSnapshot; error?: { code: string; message: string } };
    if (body.error) throw roomError(body.error.code, body.error.message);
    if (!response.ok) throw roomError("unavailable", "The room connection was interrupted. Please try again.");
    if (body.snapshot && isCurrent()) receiveSnapshot?.(body.snapshot, uid);
    return body.result as T;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") throw roomError("deadline-exceeded", "The room did not respond. Please try again.");
    throw error;
  } finally { clearTimeout(timer); }
}

type Pending = { resolve: (result: unknown) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> };
const stores = new Map<string, RoomConnection>();

export class RoomConnection {
  private state: CloudflareRoomState = { ...emptyCloudflareState };
  private listeners = new Set<() => void>();
  private socket?: WebSocket;
  private pending = new Map<string, Pending>();
  private consumers = 0;
  private active = false;
  private generation = 0;
  private reconnectAttempt = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private heartbeat?: ReturnType<typeof setTimeout>;
  private heartbeatMs?: number;
  private disposeTimer?: ReturnType<typeof setTimeout>;
  private lastPong = 0;
  private lastInteraction = 0;
  private images: Record<string, string> = {};
  private imagesGameNumber?: number;
  private imageRequests = new Map<string, Promise<string>>();

  constructor(readonly code: string, readonly userId: string) { this.scheduleDisposal(60_000); }
  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private useImageGame(gameNumber?: number) {
    if (gameNumber === undefined || gameNumber <= (this.imagesGameNumber ?? 0)) return;
    this.images = {};
    this.imageRequests.clear();
    this.imagesGameNumber = gameNumber;
  }
  cacheImage(key: string, dataUrl: string, gameNumber = this.state.snapshot?.room.gameNumber) {
    if ((gameNumber ?? 1) < (this.imagesGameNumber ?? 1)) return;
    this.useImageGame(gameNumber);
    this.images[key] = dataUrl;
  }
  getImage(key: string): Promise<string> {
    if (this.images[key]) return Promise.resolve(this.images[key]);
    const existing = this.imageRequests.get(key);
    if (existing) return existing;
    const gameNumber = this.state.snapshot?.room.gameNumber;
    const request = this.request<{ dataUrl: string }>("getRoomImage", { imageUrl: key }).then(({ dataUrl }) => {
      if (gameNumber !== this.state.snapshot?.room.gameNumber || (gameNumber ?? 1) < (this.imagesGameNumber ?? 1)) throw roomError("failed-precondition", "The game has changed.");
      this.cacheImage(key, dataUrl, gameNumber); return dataUrl;
    }).finally(() => { if (this.imageRequests.get(key) === request) this.imageRequests.delete(key); });
    this.imageRequests.set(key, request);
    return request;
  }
  private update(patch: Partial<CloudflareRoomState>) { this.state = { ...this.state, ...patch }; this.listeners.forEach((listener) => listener()); }
  private scheduleDisposal(delay: number) {
    clearTimeout(this.disposeTimer);
    this.disposeTimer = setTimeout(() => {
      if (this.consumers) return;
      this.active = false;
      this.generation += 1;
      this.stopSocket();
      this.update({ connected: false });
      const key = `${this.code}:${this.userId}`;
      if (stores.get(key) === this) stores.delete(key);
    }, delay);
  }
  private receiveSnapshot(snapshot: CloudflareSnapshot) {
    if (this.isStaleStatus(snapshot.liveStatus)) return;
    if ((snapshot.room.gameNumber ?? 1) < Math.max(this.state.snapshot?.room.gameNumber ?? 1, this.imagesGameNumber ?? 1)) return;
    this.useImageGame(snapshot.room.gameNumber);
    this.images = { ...this.images, ...snapshot.sketchAlbumPages, ...snapshot.imageAssets };
    snapshot.sketchAlbumPages = { ...this.images };
    const startedAt = snapshot.room.questionStartedAt as unknown;
    snapshot.room = { ...snapshot.room, roomBackend: "cloudflare", ...(typeof startedAt === "number" ? { questionStartedAt: { toMillis: () => startedAt } } : {}) };
    this.update({ snapshot, ...this.clockUpdate(snapshot.liveStatus), loading: false, error: "", removed: false });
    this.startHeartbeat();
  }
  private clockUpdate(status?: RoomLiveStatus): Partial<CloudflareRoomState> {
    // Calibrate on receipt, not during renders. Older Workers remain compatible.
    return typeof status?.serverNow === "number" && Number.isFinite(status.serverNow)
      ? { clockOffset: status.serverNow - Date.now() } : {};
  }
  private isStaleStatus(status?: RoomLiveStatus) {
    return status !== undefined && this.state.snapshot?.liveStatus !== undefined
      && status.revision < this.state.snapshot.liveStatus.revision;
  }
  private receiveStatus(status: RoomLiveStatus) {
    const snapshot = this.state.snapshot;
    if (!snapshot || this.isStaleStatus(status)) return;
    // Compact status describes public counters only. Keep the authoritative
    // question, private answer, cached roster pages and reveal data intact.
    this.update({ ...this.clockUpdate(status), snapshot: {
      ...snapshot,
      liveStatus: status,
      room: { ...snapshot.room, playerCount: status.playerCount, responseCount: status.responseCount },
    } });
    this.startHeartbeat();
  }
  private startHeartbeat() {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const snapshot = this.state.snapshot;
    const heartbeatMs = snapshot?.liveStatus?.heartbeatMs ?? ((snapshot?.room.playerCount ?? 0) > 20 ? 30_000 : 5_000);
    if (this.heartbeat !== undefined && this.heartbeatMs === heartbeatMs) return;
    clearTimeout(this.heartbeat);
    this.heartbeatMs = heartbeatMs;
    const generation = this.generation;
    const ping = () => {
      if (generation !== this.generation || !this.active || this.state.closedReason || this.socket !== socket || socket.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastPong > 90_000) { socket.close(); return; }
      socket.send(JSON.stringify({ type: "ping" }));
      this.heartbeat = setTimeout(ping, heartbeatMs);
    };
    // Spread a room-wide mode change over a short window so every client does
    // not hit the Durable Object on the same millisecond. Later pings use the
    // advertised cadence; regular snapshots never postpone the next ping.
    this.heartbeat = setTimeout(ping, heartbeatMs * (0.8 + Math.random() * 0.2));
  }

  retain() {
    clearTimeout(this.disposeTimer);
    this.consumers += 1;
    if (!this.active && !this.state.closedReason) { this.active = true; void this.connect(); }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.consumers = Math.max(0, this.consumers - 1);
      if (!this.consumers) {
        // React remounts and route transitions often replace a subscriber in
        // the same frame. Keep its authenticated connection warm briefly.
        this.scheduleDisposal(250);
      }
    };
  }

  private failPending(error: Error) {
    for (const request of this.pending.values()) { clearTimeout(request.timer); request.reject(error); }
    this.pending.clear();
  }
  private stopSocket() {
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.heartbeat);
    this.heartbeat = undefined;
    this.heartbeatMs = undefined;
    const socket = this.socket;
    this.socket = undefined;
    if (socket) { socket.onclose = null; socket.onmessage = null; socket.onerror = null; socket.close(); }
    this.failPending(roomError("unavailable", "The room connection was interrupted. Please try again."));
  }
  private closed(reason: string, message: string) {
    this.generation += 1;
    this.update({ loading: false, connected: false, error: "", closedReason: reason, closedMessage: message, removed: reason === "removed" || reason === "revoked" });
    this.stopSocket();
  }
  private retry() {
    if (!this.active || this.state.closedReason) return;
    this.update({ connected: false, error: "Reconnecting to the room…" });
    this.reconnectTimer = setTimeout(() => { void this.connect(); }, Math.min(15_000, 500 * 2 ** this.reconnectAttempt++));
  }
  private async connect() {
    const generation = ++this.generation;
    this.stopSocket();
    const current = () => generation === this.generation && this.active && !this.state.closedReason;
    const failed = (error: unknown) => {
      if (!current()) return;
      const code = error && typeof error === "object" && "code" in error ? String(error.code) : "";
      if (["functions/not-found", "functions/room-closed", "functions/failed-precondition", "functions/gone"].includes(code)) this.closed("unavailable", error instanceof Error ? error.message : "This room is no longer available. Start a new room to play again.");
      else if (code === "functions/permission-denied") this.closed("removed", error instanceof Error ? error.message : "You no longer have access to this room.");
      else { this.update({ loading: false }); this.retry(); }
    };
    try {
      // The socket handshake authenticates membership and room lifetime itself.
      // Firebase refreshes expired tokens; a forced refresh here adds a network
      // round trip even when the existing token is still valid.
      const { uid, idToken, appCheckToken, isCurrent } = await credentials();
      if (!current()) return;
      if (uid !== this.userId || !isCurrent()) { this.closed("session-changed", "Your sign-in changed. Join the room again."); return; }
      const url = new URL(`${roomsApiUrl()}/rooms/${encodeURIComponent(this.code)}/socket`, window.location.origin);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(url.toString(), ["sidequest", `firebase.${idToken}`, ...(appCheckToken ? [`appcheck.${appCheckToken}`] : [])]);
      let opened = false;
      this.socket = socket;
      this.reconnectTimer = setTimeout(() => { if (current() && socket.readyState !== WebSocket.OPEN) socket.close(); }, 15_000);
      socket.onopen = () => {
        if (!current()) return;
        opened = true;
        clearTimeout(this.reconnectTimer);
        this.reconnectAttempt = 0;
        this.lastPong = Date.now();
        this.update({ connected: true, error: "" });
        this.startHeartbeat();
      };
      socket.onmessage = (event) => {
        if (!current()) return;
        let message: { type: string; id?: string; result?: unknown; error?: { code: string; message: string }; snapshot?: CloudflareSnapshot; status?: RoomLiveStatus; reason?: string; message?: string; key?: string; dataUrl?: string; gameNumber?: number };
        try { message = JSON.parse(String(event.data)); } catch { return; }
        this.lastPong = Date.now();
        if (message.type === "snapshot" && message.snapshot) {
          this.receiveSnapshot(message.snapshot);
        } else if (message.type === "pong" && message.status) {
          this.receiveStatus(message.status);
        } else if (message.type === "asset" && message.key && message.dataUrl) {
          if ((message.gameNumber ?? 1) >= (this.state.snapshot?.room.gameNumber ?? 1)) this.cacheImage(message.key, message.dataUrl, message.gameNumber);
        } else if (message.type === "response" && message.id) {
          const request = this.pending.get(message.id);
          if (!request) return;
          clearTimeout(request.timer); this.pending.delete(message.id);
          if (message.error) request.reject(roomError(message.error.code, message.error.message));
          else request.resolve(message.result);
        } else if (message.type === "closed") this.closed(message.reason || "closed", message.message || "This room is closed. Start a new room to play again.");
      };
      socket.onclose = () => {
        if (!current()) return;
        this.stopSocket();
        if (opened) { this.retry(); return; }
        // Browsers hide a rejected upgrade's HTTP body. Diagnose only failed
        // handshakes so closed/revoked rooms still produce the correct message.
        void httpRpc<RoomIdentity>(this.code, "getRoomIdentity", { code: this.code }).then(() => {
          if (current()) this.retry();
        }).catch(failed);
      };
      socket.onerror = () => { if (current()) socket.close(); };
    } catch (error) { failed(error); }
  }
  interaction(event: Event) {
    if (!event.isTrusted || Date.now() - this.lastInteraction < 30_000 || this.socket?.readyState !== WebSocket.OPEN || this.state.closedReason) return;
    this.lastInteraction = Date.now();
    this.socket.send(JSON.stringify({ type: "interaction" }));
  }
  request<T>(method: string, data: Record<string, unknown>, timeout = 30_000): Promise<T> {
    if (this.state.closedReason) return Promise.reject(roomError("failed-precondition", this.state.closedMessage || "This room is closed."));
    // Freeze the game this command belongs to before waiting for credentials or
    // transport. Delayed commands must never mutate a later game in this room.
    if (data.gameNumber === undefined && this.state.snapshot?.room.gameNumber !== undefined) {
      data = { ...data, gameNumber: this.state.snapshot.room.gameNumber };
    }
    if (this.socket?.readyState !== WebSocket.OPEN) {
      const previousSnapshot = this.state.snapshot;
      if (!this.active) clearTimeout(this.disposeTimer);
      return httpRpc<T>(this.code, method, data, timeout, (snapshot, userId) => {
        if (userId === this.userId && !this.state.closedReason && this.state.snapshot === previousSnapshot) this.receiveSnapshot(snapshot);
      }).finally(() => {
        // Entry probes that never navigate into a room must not accumulate
        // private snapshots, image caches, or failed connection stores forever.
        if (!this.consumers) this.scheduleDisposal(this.active ? 250 : 60_000);
      });
    }
    const socket = this.socket;
    const id = crypto.randomUUID();
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => { this.pending.delete(id); reject(roomError("deadline-exceeded", "The room did not respond. Please try again.")); }, timeout);
      this.pending.set(id, { resolve: (value) => resolve(value as T), reject, timer });
      try { socket.send(JSON.stringify({ type: "request", id, method, data })); }
      catch { clearTimeout(timer); this.pending.delete(id); reject(roomError("unavailable", "The room connection was interrupted. Please try again.")); }
    });
  }
}

export function cloudflareRoomStore(code: string, userId: string) {
  const key = `${code}:${userId}`;
  let store = stores.get(key);
  if (!store) { store = new RoomConnection(code, userId); stores.set(key, store); }
  return store;
}
export async function roomRpc<T>(code: string, method: string, data: Record<string, unknown>, timeout?: number): Promise<T> {
  const { auth } = await import("./firebase");
  if (!auth.currentUser) throw roomError("unauthenticated", "Sign in before joining a room.");
  return cloudflareRoomStore(code, auth.currentUser.uid).request<T>(method, data, timeout);
}
export function cacheRoomImage(code: string, userId: string, imageUrl: string, dataUrl: string, gameNumber?: number) {
  cloudflareRoomStore(code, userId).cacheImage(imageUrl, dataUrl, gameNumber);
}
export async function getRoomImage(code: string, imageUrl: string) {
  const { auth } = await import("./firebase");
  if (!auth.currentUser) throw roomError("unauthenticated", "Sign in before loading a room image.");
  return cloudflareRoomStore(code, auth.currentUser.uid).getImage(imageUrl);
}
